import { useState, useEffect, useRef } from "react";
import { createRequest, getModels, getTeams } from "../api";

const DOMAINS = ["general", "medical", "financial", "legal", "hr", "other"];

// ── Additional-users email modal ───────────────────────────────────────────────
function AdditionalEmailsModal({ count, requesterEmail, initial, onApply, onClose }) {
  // count = expected_users - 1  (how many extra emails to collect)
  const [emails, setEmails] = useState(() => {
    const arr = [...(initial || [])];
    while (arr.length < count) arr.push("");
    return arr.slice(0, count);
  });
  const [error, setError] = useState("");

  const set = (idx, val) =>
    setEmails((prev) => prev.map((e, i) => (i === idx ? val : e)));

  const validate = () => {
    const trimmed = emails.map((e) => e.trim().toLowerCase());

    // Check all filled
    if (trimmed.some((e) => !e))
      return "Please fill in all email addresses.";

    // Basic email format
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (trimmed.some((e) => !re.test(e)))
      return "One or more email addresses are invalid.";

    // All different from each other
    const uniq = new Set(trimmed);
    if (uniq.size !== trimmed.length)
      return "All email addresses must be unique.";

    // None equal to requester
    if (trimmed.includes(requesterEmail.trim().toLowerCase()))
      return `Email addresses cannot match the requester's email (${requesterEmail}).`;

    return null;
  };

  const apply = () => {
    const err = validate();
    if (err) { setError(err); return; }
    onApply(emails.map((e) => e.trim().toLowerCase()));
  };

  // Close on Escape
  const modalRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    // Backdrop
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div ref={modalRef} style={{
        background: "var(--surface)",
        borderRadius: 12,
        boxShadow: "0 8px 40px rgba(0,0,0,0.22)",
        width: "100%", maxWidth: 480,
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg)",
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              👥 Additional User Emails
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
              {count} more email{count > 1 ? "s" : ""} needed · all must be unique
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer",
                     fontSize: 20, color: "var(--muted)", lineHeight: 1, padding: 4 }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Requester's own email — shown for reference */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 12px", borderRadius: 7, marginBottom: 14,
            background: "var(--success-light, #f0fff4)",
            border: "1px solid var(--success)",
            fontSize: 13,
          }}>
            <span style={{ color: "var(--success)", fontWeight: 700 }}>User 1 (you)</span>
            <span style={{ color: "var(--muted)" }}>{requesterEmail}</span>
          </div>

          {emails.map((val, idx) => (
            <div key={idx} className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13 }}>User {idx + 2} Email *</label>
              <input
                type="email"
                placeholder={`user${idx + 2}@company.com`}
                value={val}
                onChange={(e) => set(idx, e.target.value)}
                autoFocus={idx === 0}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
              />
            </div>
          ))}

          {error && (
            <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", gap: 10, justifyContent: "flex-end",
          padding: "14px 24px",
          borderTop: "1px solid var(--border)",
          background: "var(--bg)",
        }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={apply}>
            Confirm {count} Email{count > 1 ? "s" : ""} ✓
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main form ──────────────────────────────────────────────────────────────────
export default function RequestForm({ onSuccess, prefillUser = null }) {
  const [form, setForm] = useState({
    title:             "",
    requester:         prefillUser?.name  || "",
    requester_email:   prefillUser?.email || "",
    manager_email:     "",
    department:        "",
    team_id:           "",
    use_case:          "",
    domain:            "general",
    involves_pii:      false,
    expected_users:    1,
    preferred_model_id: "",
  });
  const [additionalEmails, setAdditionalEmails] = useState([]);  // confirmed extra emails
  const [showModal,   setShowModal]   = useState(false);
  const [models,      setModels]      = useState([]);
  const [teams,       setTeams]       = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");
  const [result,      setResult]      = useState(null);

  useEffect(() => {
    getModels(true).then(setModels).catch(() => {});
    getTeams().then(setTeams).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When expected_users decreases, trim additionalEmails to fit
  const handleExpectedUsersChange = (raw) => {
    const n = Math.max(1, parseInt(raw, 10) || 1);
    set("expected_users", n);
    const needed = n - 1;
    setAdditionalEmails((prev) => prev.slice(0, needed));
  };

  const extraNeeded = Number(form.expected_users) - 1;
  const emailsConfirmed = additionalEmails.length === extraNeeded && extraNeeded > 0;
  const emailsPartial   = additionalEmails.length > 0 && additionalEmails.length < extraNeeded;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // If extra users needed but emails not confirmed, open modal instead
    if (extraNeeded > 0 && additionalEmails.length < extraNeeded) {
      setShowModal(true);
      return;
    }
    setError("");
    setResult(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expected_users:         Number(form.expected_users),
        preferred_model_id:     form.preferred_model_id ? Number(form.preferred_model_id) : null,
        team_id:                form.team_id ? Number(form.team_id) : null,
        additional_user_emails: additionalEmails,
      };
      const res = await createRequest(payload);
      setResult(res);
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({
      title: "",
      requester:       prefillUser?.name  || "",
      requester_email: prefillUser?.email || "",
      manager_email: "", department: "", team_id: "",
      use_case: "", domain: "general",
      involves_pii: false, expected_users: 1, preferred_model_id: "",
    });
    setAdditionalEmails([]);
    setResult(null);
    setError("");
  };

  // ── Success screen ──
  if (result) {
    const riskColors = {
      low:      "var(--success)",
      medium:   "var(--warning)",
      high:     "var(--danger)",
      critical: "#9b2c2c",
    };
    return (
      <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
        <div className="card-body" style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <h2 style={{ marginBottom: 8 }}>Request Submitted</h2>
          <p style={{ color: "var(--muted)", marginBottom: 20 }}>
            Your request has been verified and your manager ({result.manager_email}) has been notified for review.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24, textAlign: "left" }}>
            <div className="stat-tile">
              <div className="label">Risk Score</div>
              <div className="value" style={{ color: riskColors[result.risk_level] }}>{result.risk_score}</div>
              <div className="sub">out of 10</div>
            </div>
            <div className="stat-tile">
              <div className="label">Risk Level</div>
              <div className="value" style={{ fontSize: 20, color: riskColors[result.risk_level], textTransform: "capitalize" }}>
                {result.risk_level}
              </div>
            </div>
          </div>
          {result.verification_notes && (
            <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6,
                          padding: "12px 16px", textAlign: "left", marginBottom: 20, fontSize: 13, color: "var(--muted)" }}>
              <strong style={{ color: "var(--text)" }}>Verification notes:</strong>
              <p style={{ marginTop: 4 }}>{result.verification_notes}</p>
            </div>
          )}
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6,
                        padding: "10px 14px", marginBottom: 20, fontSize: 13, color: "#1d4ed8" }}>
            📧 Your manager at <strong>{result.manager_email}</strong> has been notified.
            You'll receive an email at <strong>{result.requester_email}</strong> once a decision is made.
          </div>
          <button className="btn btn-primary" onClick={reset}>Submit Another Request</button>
        </div>
      </div>
    );
  }

  const isUserLocked = Boolean(prefillUser);

  return (
    <>
      {/* Additional emails modal */}
      {showModal && (
        <AdditionalEmailsModal
          count={extraNeeded}
          requesterEmail={form.requester_email}
          initial={additionalEmails}
          onApply={(emails) => { setAdditionalEmails(emails); setShowModal(false); }}
          onClose={() => setShowModal(false)}
        />
      )}

      <div className="card" style={{ maxWidth: 660, margin: "0 auto" }}>
        <div className="card-header">New AI Use-Case Request</div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>

            {/* Basic info */}
            <div className="form-group">
              <label>Request Title *</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Customer churn prediction model" required />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Your Name *</label>
                <input value={form.requester} onChange={(e) => set("requester", e.target.value)}
                  placeholder="Full name" required
                  readOnly={isUserLocked}
                  style={isUserLocked ? { background: "var(--bg)", color: "var(--muted)", cursor: "not-allowed" } : {}}
                />
              </div>
              <div className="form-group">
                <label>Team</label>
                <select value={form.team_id} onChange={(e) => {
                  const tid = e.target.value;
                  set("team_id", tid);
                  if (tid) {
                    const t = teams.find((x) => String(x.id) === tid);
                    if (t) set("manager_email", t.manager_email);
                  }
                }}>
                  <option value="">— Select team (optional) —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.department || t.manager_name})</option>
                  ))}
                </select>
                <div className="form-hint">Selecting a team auto-fills the manager email.</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Department</label>
                <input value={form.department} onChange={(e) => set("department", e.target.value)}
                  placeholder="e.g. Engineering" />
              </div>
              <div style={{ flex: 1 }} />
            </div>

            {/* Email fields */}
            <div className="form-row">
              <div className="form-group">
                <label>Your Email *</label>
                <input type="email" value={form.requester_email}
                  onChange={(e) => set("requester_email", e.target.value)}
                  placeholder="you@company.com" required
                  readOnly={isUserLocked}
                  style={isUserLocked ? { background: "var(--bg)", color: "var(--muted)", cursor: "not-allowed" } : {}}
                />
                <div className="form-hint">You'll receive the API key here on approval.</div>
              </div>
              <div className="form-group">
                <label>Manager's Email *</label>
                <input type="email" value={form.manager_email}
                  onChange={(e) => set("manager_email", e.target.value)}
                  placeholder="manager@company.com" required />
                <div className="form-hint">They'll be notified to review this request.</div>
              </div>
            </div>

            {/* Use case */}
            <div className="form-group">
              <label>Use-Case Description *</label>
              <textarea value={form.use_case}
                onChange={(e) => set("use_case", e.target.value)}
                placeholder="Describe what the AI will do, what data it accesses, and how results will be used…"
                required />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Domain *</label>
                <select value={form.domain} onChange={(e) => set("domain", e.target.value)}>
                  {DOMAINS.map((d) => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Expected users + email collection trigger */}
              <div className="form-group">
                <label>Expected Users</label>
                <input type="number" min={1} value={form.expected_users}
                  onChange={(e) => handleExpectedUsersChange(e.target.value)} />
                <div className="form-hint">How many users will interact with this?</div>
              </div>
            </div>

            {/* Additional emails section — shown when expected_users > 1 */}
            {extraNeeded > 0 && (
              <div style={{
                padding: "14px 16px", borderRadius: 8, marginBottom: 16,
                background: emailsConfirmed ? "var(--success-light, #f0fff4)" : "var(--warn-light, #fffbeb)",
                border: `1px solid ${emailsConfirmed ? "var(--success)" : "var(--warning, #d97706)"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <div style={{
                      fontWeight: 700, fontSize: 13,
                      color: emailsConfirmed ? "var(--success)" : "var(--warning, #92400e)",
                    }}>
                      {emailsConfirmed
                        ? `✓ ${extraNeeded} additional email${extraNeeded > 1 ? "s" : ""} confirmed`
                        : `⚠ ${extraNeeded} more email${extraNeeded > 1 ? "s" : ""} required`}
                    </div>
                    {emailsConfirmed ? (
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                        {additionalEmails.join(", ")}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                        You've indicated {form.expected_users} users — please add their email IDs.
                      </div>
                    )}
                  </div>
                  <button type="button"
                    onClick={() => setShowModal(true)}
                    style={{
                      flexShrink: 0,
                      padding: "6px 14px", borderRadius: 6,
                      border: `1px solid ${emailsConfirmed ? "var(--success)" : "var(--warning, #d97706)"}`,
                      background: "var(--surface)", cursor: "pointer",
                      fontSize: 12, fontWeight: 700,
                      color: emailsConfirmed ? "var(--success)" : "var(--warning, #92400e)",
                    }}>
                    {emailsConfirmed ? "Edit Emails" : "Add Emails →"}
                  </button>
                </div>
              </div>
            )}

            {/* Preferred model */}
            <div className="form-group">
              <label>
                Preferred Model{" "}
                <span style={{ fontWeight: 400, color: "var(--muted)" }}>
                  (optional — manager may change this)
                </span>
              </label>
              <select value={form.preferred_model_id}
                onChange={(e) => set("preferred_model_id", e.target.value)}>
                <option value="">Let governance auto-assign</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} — {m.provider} (tier: {m.tier})
                  </option>
                ))}
              </select>
              <div className="form-hint">
                Your preference will be honoured if it's eligible for your risk level.
              </div>
            </div>

            {/* PII */}
            <div className="form-group">
              <div className="checkbox-group">
                <input type="checkbox" id="pii" checked={form.involves_pii}
                  onChange={(e) => set("involves_pii", e.target.checked)} />
                <label htmlFor="pii" style={{ marginBottom: 0 }}>
                  This system will process Personal Identifiable Information (PII)
                </label>
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit Request"}
            </button>

            {extraNeeded > 0 && !emailsConfirmed && (
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
                Submitting will open the email prompt if you haven't added them yet.
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
}
